// configurator.js

import {
    Scene,
    Group,
    WebGLRenderer,
    HemisphereLight,
    PerspectiveCamera,
    Raycaster,
    Vector2,
    Vector3,
    Box3,
    TextureLoader,
    UVMapping,
    RepeatWrapping,
    NearestMipmapNearestFilter,
    NearestFilter
} from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader.js';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader.js';

const DISABLE_TEXTURE_CACHE = true;
const ASSEMBLY_NAME = "BeckerWeb";
const MODEL_ASPECT = 650 / 500;
const MODEL_FIT_MARGIN = 1.2;

const modelViewer = {
    // Three.js essentials
    scene: null,
    camera: null,
    renderer: null,
    controls: null,
    canvas: null,
    loadedPercent: 0,
    modelGroup: null,
    raycaster: new Raycaster(),
    mouse: new Vector2(),
    hoveredObject: null,

    // Texture utilities
    textureLoader: new TextureLoader(),
    textureCache: {},

    // --- Initialization & Rendering ---

    loadModel(canvasId, options) {

        this.canvas = document.getElementById(canvasId);
        this.renderer = new WebGLRenderer({
            canvas: this.canvas,
            antialias: true,
            alpha: true
        });
        this.renderer.setPixelRatio(window.devicePixelRatio);

        this.scene = new Scene();
        //this.scene.background.alpha = 0;
        //this.scene.background.color = new Color(0xffffff);
        this.scene.background = null;

        this.modelGroup = new Group();
        this.scene.add(this.modelGroup);

        this.scene.up.set(0, 1, 0);

        this.camera = new PerspectiveCamera(
            45,
            MODEL_ASPECT,
            0.1,
            250
        );
        this.camera.position.set(0, 100, 120);

        // The tab panel hosting the canvas is hidden until opened, so the container
        // has no width up front. Observe it instead of sizing once.
        this._resizeToContainer();
        new ResizeObserver(() => this._resizeToContainer()).observe(this.canvas.parentElement);

        const light = new HemisphereLight(0xffffff, 0x444444, 1.5);
        this.scene.add(light);

        this.controls = new OrbitControls(this.camera, this.canvas);
        this.controls.autoRotate = false; //!!options.enableRotation;
        this.controls.enableZoom = true;
        this.controls.enablePan = true;
        this.controls.update();

        this._loadModelFile(options.modelUrl);

        this.canvas.addEventListener('mousemove', e => this._handleMouseMove(e));

        this._animate();
    },
    _loadModelFile(url) {
        const ext = url.split('.').pop().toLowerCase();
        let loader;

        if (ext === 'glb' || ext === 'gltf') loader = new GLTFLoader();
        else if (ext === 'fbx') loader = new FBXLoader();
        else if (ext === 'obj') loader = new OBJLoader();
        else {
            console.error('Unsupported model format:', ext);
            return;
        }

        loader.load(url, model => {
            const obj = model.scene || model;
            //this.scene.add(obj);
            this.modelGroup.add(obj);
            this._frameModel(obj);
        }, progress => {
            this.loadedPercent = (progress.loaded / progress.total) * 100;
            //DotNet.invokeMethodAsync(ASSEMBLY_NAME, 'SetLoadedPercent', this.loadedPercent);
        });
    },
    _frameModel(object) {
        const bounds = new Box3().setFromObject(object);
        const center = bounds.getCenter(new Vector3());
        const size = bounds.getSize(new Vector3());

        // Pull back far enough for the model's largest dimension to fit the vertical fov
        const fov = this.camera.fov * (Math.PI / 180);
        const distance = (Math.max(size.x, size.y, size.z) / 2) / Math.tan(fov / 2) * MODEL_FIT_MARGIN;

        this.camera.position.set(center.x, center.y, center.z + distance);
        this.camera.near = distance / 100;
        this.camera.far = distance * 10;
        this.camera.updateProjectionMatrix();

        this.controls.target.copy(center);
        this.controls.update();

        // Reset restores whatever state was saved last, which would otherwise be the
        // pre-load placeholder view rather than this one.
        this.controls.saveState();
    },
    _resizeToContainer() {
        const width = this.canvas.parentElement.clientWidth;

        // A hidden panel reports zero, and setSize would write that onto the canvas,
        // clearing the intrinsic size it can never recover on its own.
        if (width === 0) {
            return;
        }

        // The container's own height follows the canvas, so deriving height from it
        // would be circular; hold the intended aspect instead.
        const height = Math.round(width / MODEL_ASPECT);

        this.renderer.setSize(width, height);
        this.camera.aspect = width / height;
        this.camera.updateProjectionMatrix();
    },
    _animate() {
        requestAnimationFrame(() => this._animate());
        this.controls.update();
        this.renderer.render(this.scene, this.camera);
    },
    _handleMouseMove(event) {
        const rect = this.canvas.getBoundingClientRect();
        this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

        this.raycaster.setFromCamera(this.mouse, this.camera);
        const intersects = this.raycaster.intersectObjects(this.scene.children, true);

        if (intersects.length > 0) {
            const hovered = intersects[0].object;
            if (this.hoveredObject !== hovered) {
                this.hoveredObject = hovered;
                //DotNet.invokeMethodAsync(ASSEMBLY_NAME, 'SetHoveredObject', hovered.name);
            }
        } else if (this.hoveredObject !== null) {
            this.hoveredObject = null;
            //DotNet.invokeMethodAsync(ASSEMBLY_NAME, 'SetHoveredObject', null);
        }
    },
    getAllObjectNames() {
        const names = [];
        this.scene.traverse(obj => {
            if (obj.isMesh) names.push(obj.name);
        });
        return names;
    },

    findObjectByName(name) {
        return this.scene.getObjectByName(name);
    },

    hideObject(name) {
        const obj = this.findObjectByName(name);
        if (obj) obj.visible = false;
    },

    showObject(name) {
        const obj = this.findObjectByName(name);
        if (obj) obj.visible = true;
    },

    toggleObject(name, value) {
        const obj = this.findObjectByName(name);
        if (obj) {
            obj.visible = (value === undefined) ? !obj.visible : value;
        } else {
            console.error('Could not find object:', name);
        }
    },
    colorObject(name, color) {
        const obj = this.findObjectByName(name);
        if (obj && obj.material?.color) {
            obj.material.color.setStyle(color);
        } else {
            console.error('Could not color object:', name);
        }
    },
    textureObject(name, path) {
        const obj = this.findObjectByName(name);
        if (!obj) return;

        let texture;
        if (DISABLE_TEXTURE_CACHE || !this.textureCache[path]) {
            texture = this.textureLoader.load(path, tex => {
                tex.mapping = UVMapping;
                tex.repeat = new Vector2(8, 8);
                tex.wrapS = RepeatWrapping;
                tex.wrapT = RepeatWrapping;
                tex.minFilter = NearestMipmapNearestFilter;
                tex.magFilter = NearestFilter;
            });
            this.textureCache[path] = texture;
        } else {
            texture = this.textureCache[path];
        }

        obj.material.map = texture;
        obj.material.needsUpdate = true;
    },
    untextureObject(name) {
        const obj = this.findObjectByName(name);
        if (obj && obj.material) {
            obj.material.map = null;
            obj.material.needsUpdate = true;
        }
    },
    setRotation(enabled) {
        if (this.controls) this.controls.autoRotate = !!enabled;
    },
    toggleModelVisibility(name, visible) {
        this.toggleObject(name, visible);
    },
    resetCamera() {
        if (this.controls) this.controls.reset();
    },
    /**
     * attributes: {
     *   "ObjectName": { texture: "url", color: "#hex" },
     *   ...
     * }
     */
    applyMappedAttributes(attributes) {
        if (!attributes) return;
        for (const [objName, cfg] of Object.entries(attributes)) {
            if (cfg.texture) this.textureObject(objName, cfg.texture);
            if (cfg.color) this.colorObject(objName, cfg.color);
        }
    }
};

window.modelViewer = modelViewer;